type LexicalNode = {
  type: string;
  text?: string;
  children?: LexicalNode[];
};

type LexicalJson = {
  root: LexicalNode;
};

const extractTextFromNode = (node: LexicalNode): string => {
  let text = '';

  if (node.type === 'text' && node.text) {
    text += node.text;
  } else if (node.children) {
    node.children.forEach((child) => {
      text += extractTextFromNode(child);
    });

    if (node.type === 'paragraph' || node.type === 'listitem') {
      text += '\n';
    }
  }

  return text;
};

export const extractTextFromLexicalJson = (json: LexicalJson): string => {
  const {root} = json;
  return extractTextFromNode(root).trim(); // Remove trailing newline
};

export const parseAndExtractTextFromLexicalJson = (jsonString: string): string => {
  try {
    const json = JSON.parse(jsonString);
    return extractTextFromLexicalJson(json);
  } catch {
    return jsonString; // fail gracefully by returning the original string
  }
};

export const getPlainTextFromRichTextValue = (value?: string | null): string =>
  value ? parseAndExtractTextFromLexicalJson(value) : '';

export const isLexicalJsonString = (value?: string | null): boolean => {
  if (!value) {
    return false;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed?.root?.type === 'root' && Array.isArray(parsed?.root?.children);
  } catch {
    return false;
  }
};

/** Empty paragraph used as vertical spacing between Lexical blocks (e.g. around horizontal rules). */
export const lexicalEmptyParagraphNode = {
  type: 'paragraph',
  version: 1,
  children: [],
} as const;

const spacerNode = lexicalEmptyParagraphNode;

const titleNode = (title: string, headerStyle: string) => ({
  type: 'heading',
  tag: 'h1',
  version: 1,
  children: [textNode(title, headerStyle)],
});

const textNode = (title: string, style: string = '') => ({
  detail: 0,
  format: 1,
  mode: 'normal',
  style,
  text: title + ': ',
  type: 'text',
  version: 1,
});

export const insertTitle = (
  json: any,
  title: string,
  spacer: boolean,
  headerStyle: string = ''
) => {
  const children = !json
    ? []
    : json?.root?.children?.[0]?.type === 'paragraph'
      ? [
          ...(spacer ? [spacerNode] : []),
          {
            ...json.root.children[0],
            children: [textNode(title, headerStyle), ...(json.root.children[0] as any).children],
          },
          ...json.root.children.slice(1),
        ]
      : [...(spacer ? [spacerNode] : []), titleNode(title, headerStyle), ...json.root.children];

  let lastChildrenNodeWithChildren = children.length - 1;

  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i] as any;
    if (child && child.children && child.children.length) {
      lastChildrenNodeWithChildren = i;
      break;
    }
  }

  return children.slice(0, lastChildrenNodeWithChildren + 1);
};

export const toLexicalJson = (child: any) => {
  return JSON.stringify({
    root: {
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
      children: [
        {
          children: [child],
          format: '',
          type: 'paragraph',
          version: 1,
          textFormat: 0,
          textStyle: '',
        },
      ],
    },
  });
};

export const toPlainTextLexicalJson = (text: string) =>
  toLexicalJson({
    detail: 0,
    format: 0,
    mode: 'normal',
    style: '',
    text,
    type: 'text',
    version: 1,
  });
